import { useEffect, useMemo, useRef, useState } from "react";
import { Command, CornerDownLeft, Search } from "lucide-react";
import { NAV } from "./OperatorNav";

const fuzzy = (needle, hay) => {
  const n = needle.toLowerCase().replace(/\s+/g, "");
  const h = hay.toLowerCase();
  if (!n) return 0;
  let i = 0;
  let score = 0;
  for (const ch of h) {
    if (ch === n[i]) {
      i += 1;
      score += 2;
    }
    if (i === n.length) break;
  }
  if (i < n.length) return -1;
  return score + (h.startsWith(n[0]) ? 3 : 0);
};

/** Jump anywhere: ⌘K on a Mac, Alt+K or Ctrl+K elsewhere. Two letters is usually enough. */
export const CommandPalette = ({ subjects, onView, onSubject }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const field = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      if (k === "k" && (e.metaKey || e.ctrlKey || e.altKey)) {
        e.preventDefault();
        setOpen((v) => !v);
        setQ("");
        setCursor(0);
      }
      if (k === "escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) field.current?.focus();
  }, [open]);

  const items = useMemo(() => {
    const views = NAV.flatMap(({ group, items: list }) =>
      list.map((t) => ({ id: `view:${t}`, label: t, hint: group, kind: "view", value: t }))
    );
    const people = (subjects || []).map((s) => ({
      id: `subject:${s.subject}`,
      label: s.name && s.name !== "—" ? s.name : s.subject.slice(0, 10),
      hint: `C ${s.predictive_confidence}% · ${s.coverage.filled}/${s.coverage.total ?? 46}`,
      kind: "subject",
      value: s.subject,
    }));
    const all = [...views, ...people];
    if (!q.trim()) return all;
    return all
      .map((it) => ({ it, s: fuzzy(q, `${it.label} ${it.hint}`) }))
      .filter((r) => r.s >= 0)
      .sort((a, b) => b.s - a.s)
      .map((r) => r.it);
  }, [subjects, q]);

  const pick = (item) => {
    if (!item) return;
    if (item.kind === "view") onView(item.value);
    else onSubject(item.value);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="command-palette"
      className="fixed inset-0 z-[80] flex items-start justify-center bg-[rgba(2,2,4,0.72)] px-4 pt-[14vh] backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="panel w-full max-w-xl overflow-hidden">
        <div className="flex items-center gap-3 border-b border-[var(--line)] px-5 py-4">
          <Search size={13} strokeWidth={1.7} className="text-zinc-600" />
          <input
            ref={field}
            data-testid="command-palette-input"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCursor(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, items.length - 1));
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              }
              if (e.key === "Enter") pick(items[cursor]);
            }}
            placeholder="jump to a view or a subject"
            className="w-full bg-transparent font-data text-[13px] text-zinc-100 outline-none placeholder:text-zinc-700"
          />
          <span className="label whitespace-nowrap !text-[9px]">esc</span>
        </div>

        <ul className="thin-scroll max-h-[22rem] overflow-y-auto py-1">
          {items.map((it, i) => (
            <li key={it.id}>
              <button
                data-testid={`command-item-${it.id}`}
                onMouseEnter={() => setCursor(i)}
                onClick={() => pick(it)}
                className={`flex w-full items-baseline justify-between gap-4 px-5 py-2.5 text-left transition-colors ${
                  i === cursor ? "bg-[rgba(255,255,255,0.06)]" : ""
                }`}
              >
                <span
                  className={`font-data text-[12px] ${
                    i === cursor ? "text-zinc-100" : "text-zinc-400"
                  }`}
                >
                  {it.label}
                </span>
                <span className="label !text-[9px]">
                  {it.kind === "subject" ? "subject · " : ""}
                  {it.hint}
                </span>
              </button>
            </li>
          ))}
          {items.length === 0 && (
            <li className="px-5 py-4 font-data text-[11px] text-zinc-600">Nothing matches.</li>
          )}
        </ul>

        <div className="flex items-center gap-5 border-t border-[var(--line)] px-5 py-3">
          <span className="label flex items-center gap-1.5 !text-[9px]">
            <CornerDownLeft size={10} strokeWidth={1.8} /> open
          </span>
          <span className="label !text-[9px]">↑↓ move</span>
          <span className="label ml-auto flex items-center gap-1.5 !text-[9px]">
            <Command size={10} strokeWidth={1.8} />K · alt+K
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
